import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import api from '../api';
import { CHAT_READ_EVENT } from './useUnreadMessages';
const merge = (oldRows, newRows) => [...new Map([...oldRows, ...newRows].map(row => [row.id, row])).values()].sort((a, b) => a.id - b.id);
export default function useChatMessages(threadId) {
  const [messages, setMessages] = useState([]);
  const [loadedThread, setLoadedThread] = useState(threadId);
  const [loading, setLoading] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [hasOlder, setHasOlder] = useState(false);
  const [error, setError] = useState('');
  const scrollRef = useRef(null);
  const messageRows = useRef([]);
  const receiptOffset = useRef(0);
  useEffect(() => { messageRows.current = messages; }, [messages]);
  const current = useRef(threadId);
  current.current = threadId;
  const version = useRef(0),
    requestNumber = useRef(0),
    scrollPlan = useRef(null),
    oldest = useRef(null),
    newest = useRef(null);
  const refresh = useCallback(async () => {
    if (!threadId || document.hidden) return;
    const generation = version.current,
      sequence = ++requestNumber.current;
    try {
      const pending = messageRows.current.filter(message => !message.read_at);
      const offset = pending.length ? receiptOffset.current % pending.length : 0;
      const receiptIds = [...pending.slice(offset), ...pending.slice(0, offset)].slice(0, 300).map(message => message.id);
      receiptOffset.current = offset + receiptIds.length;
      const response = await api.get(`/chat/threads/${threadId}/messages`, {
        params: {
          limit: 200,
          ...(newest.current ? {after_id: newest.current} : {}),
          ...(receiptIds.length ? {receipt_ids: receiptIds} : {}),
          mark_read: !document.hidden
        }
      });
      if (current.current !== threadId || generation !== version.current || sequence !== requestNumber.current) return;
      const rows = Array.isArray(response.data?.messages) ? response.data.messages : [];
      const box = scrollRef.current;
      if (!box || box.scrollHeight - box.scrollTop - box.clientHeight < 90) scrollPlan.current = {
        bottom: true
      };
      const receipts = new Map((response.data?.read_receipts || []).map(receipt => [receipt.id, receipt.read_at]));
      setMessages(previous => merge(previous, rows).map(message => receipts.has(message.id) ? {...message, read_at: receipts.get(message.id)} : message));
      if (rows.length) window.dispatchEvent(new Event(CHAT_READ_EVENT));
      if (rows.length) newest.current = rows[rows.length - 1].id;
      if (oldest.current === null) {
        setHasOlder(Boolean(response.data?.has_older));
        oldest.current = rows[0]?.id ?? null;
        scrollPlan.current = {
          bottom: true
        };
      }
      setError('');
    } catch {
      if (current.current === threadId && generation === version.current && sequence === requestNumber.current) setError('Unable to load messages. Please retry.');
    } finally {
      if (current.current === threadId && generation === version.current) setLoading(false);
    }
  }, [threadId]);
  const loadOlder = async () => {
    if (!threadId || loadingOlder || !oldest.current) return;
    const generation = version.current;
    setLoadingOlder(true);
    try {
      const response = await api.get(`/chat/threads/${threadId}/messages`, {
        params: {
          limit: 200,
          before_id: oldest.current,
          mark_read: !document.hidden
        }
      });
      if (current.current !== threadId || generation !== version.current) return;
      const rows = Array.isArray(response.data?.messages) ? response.data.messages : [];
      const box = scrollRef.current;
      if (box) scrollPlan.current = {
        height: box.scrollHeight,
        top: box.scrollTop
      };
      setMessages(previous => merge(previous, rows));
      if (rows.length && !document.hidden) window.dispatchEvent(new Event(CHAT_READ_EVENT));
      setHasOlder(Boolean(response.data?.has_older));
      oldest.current = rows[0]?.id || oldest.current;
      setError('');
    } catch {
      if (current.current === threadId && generation === version.current) setError('Unable to load older messages. Please retry.');
    } finally {
      if (current.current === threadId && generation === version.current) setLoadingOlder(false);
    }
  };
  useEffect(() => {
    version.current++;
    oldest.current = null;
    newest.current = null;
    messageRows.current = [];
    receiptOffset.current = 0;
    setLoadedThread(threadId);
    setMessages([]);
    setHasOlder(false);
    setError('');
    setLoading(Boolean(threadId));
    setLoadingOlder(false);
    refresh();
    let timer;
    let stopped = false;
    const run = async () => {
      if (stopped) return;
      if (!document.hidden) await refresh();
      if (!stopped) timer = setTimeout(run, 3000);
    };
    if (threadId) timer = setTimeout(run, 3000);
    const visible = () => {
      if (!document.hidden) refresh();
    };
    document.addEventListener('visibilitychange', visible);
    return () => {
      stopped = true;
      // Invalidate requests started after setup; this counter is not a DOM ref.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      version.current++;
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', visible);
    };
  }, [threadId, refresh]);
  useLayoutEffect(() => {
    const box = scrollRef.current,
      plan = scrollPlan.current;
    if (box && plan) {
      box.scrollTop = plan.bottom ? box.scrollHeight : plan.top + box.scrollHeight - plan.height;
      scrollPlan.current = null;
    }
  }, [messages]);
  return {
    messages: loadedThread === threadId ? messages : [],
    loading: loading || Boolean(threadId && loadedThread !== threadId),
    loadingOlder,
    hasOlder: loadedThread === threadId && hasOlder,
    error,
    scrollRef,
    refresh,
    loadOlder
  };
}
